import { 
  UserSearch, Award, TrendingUp, Users, Target, Settings,
  Shield, Zap, Lock, Clock, Database, BarChart3, 
  CheckCircle, Globe, Brain, Sparkles, FileText, Calendar,
  Search, Briefcase, MessageSquare, HeadphonesIcon, Network,
  Workflow, Building, Star, Fingerprint, AlertTriangle,
  GitBranch, Phone, Camera, Monitor, Layers, Activity, FileCheck
} from 'lucide-react';
import { ProductConfigs } from './types';

export const recruitmentSolutionsConfigs: ProductConfigs = {
  'recruitment-training-platform': {
    keyBenefits: [
      {
        icon: Award,
        title: 'Advanced Certification Management',
        description: 'Comprehensive certification tracking with industry-standard credentials, skill validation, and continuous professional development.',
        metrics: '95% certification completion rate',
        color: 'from-blue-500 to-indigo-600'
      },
      {
        icon: Brain,
        title: 'AI-Powered Learning Paths',
        description: 'Personalized learning experiences with adaptive content delivery, skill gap analysis, and intelligent recommendations.',
        metrics: '80% faster skill development',
        color: 'from-purple-500 to-pink-600'
      },
      {
        icon: Users,
        title: 'Interactive Training Modules',
        description: 'Immersive training experiences with VR simulations, role-playing scenarios, and real-world practice exercises.',
        metrics: '90% engagement improvement',
        color: 'from-green-500 to-emerald-600'
      },
      {
        icon: BarChart3,
        title: 'Performance Analytics Dashboard',
        description: 'Comprehensive tracking of learning progress, skill development, and training ROI with detailed reporting.',
        metrics: 'Data-driven training insights',
        color: 'from-orange-500 to-red-600'
      },
      {
        icon: Globe,
        title: 'Industry Best Practices Library',
        description: 'Extensive repository of recruitment best practices, case studies, and expert guidance from industry leaders.',
        metrics: '10,000+ best practice resources',
        color: 'from-teal-500 to-blue-600'
      },
      {
        icon: Target,
        title: 'Competency Assessment Engine',
        description: 'Advanced skill assessment with real-time feedback, competency mapping, and professional development planning.',
        metrics: '360-degree skill evaluation',
        color: 'from-indigo-500 to-purple-600'
      }
    ],
    advancedCapabilities: [
      {
        icon: Sparkles,
        title: 'VR Interview Simulation',
        description: 'Immersive virtual reality training for interview techniques, candidate assessment, and difficult conversation handling.',
        metrics: 'Realistic training scenarios',
        color: 'from-violet-500 to-purple-600'
      },
      {
        icon: Settings,
        title: 'Custom Training Workflows',
        description: 'Configurable learning paths with role-specific training, department customisation, and adaptive progression.',
        metrics: 'Unlimited customisation',
        color: 'from-emerald-500 to-green-600'
      },
      {
        icon: FileText,
        title: 'Compliance Training Automation',
        description: 'Automated compliance tracking with regulatory updates, mandatory training alerts, and certification renewals.',
        metrics: '100% compliance tracking',
        color: 'from-cyan-500 to-teal-600'
      },
      {
        icon: Calendar,
        title: 'Mentorship Program Management',
        description: 'Structured mentorship programs with mentor matching, progress tracking, and relationship management tools.',
        metrics: 'Enhanced career development',
        color: 'from-yellow-500 to-orange-600'
      },
      {
        icon: TrendingUp,
        title: 'Learning Impact Analytics',
        description: 'Measure training effectiveness with performance correlation, business impact analysis, and ROI calculations.',
        metrics: 'Proven training ROI',
        color: 'from-rose-500 to-pink-600'
      },
      {
        icon: Brain,
        title: 'Gamified Learning Experience',
        description: 'Engaging gamification with achievement systems, leaderboards, and interactive learning challenges.',
        metrics: 'Increased engagement rates',
        color: 'from-blue-500 to-cyan-600'
      }
    ],
    securityPerformance: [
      {
        icon: Shield,
        title: 'Compliance Excellence Framework',
        description: 'SOC 2 Type II, ISO 27001, GDPR compliance with continuous auditing, regulatory reporting, and industry standard certifications.',
        metrics: 'Enterprise-grade compliance',
        color: 'from-red-500 to-red-600'
      },
      {
        icon: BarChart3,
        title: 'Security Performance Analytics',
        description: 'Real-time threat monitoring, security incident analytics, and performance metrics with advanced dashboard visualization.',
        metrics: '24/7 security monitoring',
        color: 'from-blue-500 to-cyan-600'
      },
      {
        icon: Zap,
        title: 'Scalable Security Infrastructure',
        description: '99.99% uptime guarantee with auto-scaling security services, load balancing, and distributed protection mechanisms.',
        metrics: 'Guaranteed system availability',
        color: 'from-green-500 to-emerald-600'
      },
      {
        icon: Globe,
        title: 'Advanced Firewall & DDoS Protection',
        description: 'Enterprise-grade firewall protection with intelligent DDoS mitigation, traffic filtering, and threat prevention.',
        metrics: 'Multi-layer threat protection',
        color: 'from-purple-500 to-pink-600'
      },
      {
        icon: Lock,
        title: 'Multi-Factor Authentication Suite',
        description: 'Comprehensive MFA with biometric authentication, hardware tokens, and adaptive authentication protocols.',
        metrics: 'Advanced identity verification',
        color: 'from-orange-500 to-red-600'
      },
      {
        icon: Target,
        title: 'Security Information & Event Management',
        description: 'AI-powered SIEM with real-time monitoring, automated incident response, and comprehensive security orchestration.',
        metrics: 'Intelligent threat detection',
        color: 'from-teal-500 to-blue-600'
      },
      {
        icon: Database,
        title: 'Data Loss Prevention & Backup',
        description: 'Encrypted data protection with automated backups, disaster recovery, and point-in-time restoration capabilities.',
        metrics: 'Zero data loss guarantee',
        color: 'from-indigo-500 to-purple-600'
      },
      {
        icon: Settings,
        title: 'Network Segmentation & Monitoring',
        description: 'Advanced network isolation with micro-segmentation, traffic analysis, and real-time network security monitoring.',
        metrics: 'Enhanced network security',
        color: 'from-cyan-500 to-teal-600'
      },
      {
        icon: CheckCircle,
        title: 'Continuous Security Assessment',
        description: 'Automated vulnerability scanning, penetration testing, and continuous security validation with remediation guidance.',
        metrics: 'Proactive security testing',
        color: 'from-yellow-500 to-orange-600'
      }
    ]
  },

  'talent-acquisition-suite': {
    keyBenefits: [
      {
        icon: UserSearch,
        title: 'AI-Powered Multi-Channel Sourcing',
        description: 'Intelligent talent sourcing across multiple channels with AI-powered candidate matching, social recruiting, and passive candidate engagement throughout the UK and globally.',
        metrics: '300% increase in qualified candidates',
        color: 'from-green-500 to-teal-600'
      },
      {
        icon: Brain,
        title: 'Intelligent Candidate Matching & Scoring',
        description: 'Advanced machine learning algorithms for precise candidate-role matching with predictive success scoring, cultural fit analysis, and comprehensive candidate profiling.',
        metrics: '85% improvement in hire quality',
        color: 'from-purple-500 to-pink-600'
      },
      {
        icon: Globe,
        title: 'Global Recruitment Network Access',
        description: 'Worldwide talent acquisition capabilities with multi-region support, local compliance management, cultural adaptation, and international recruitment expertise.',
        metrics: 'Access to global talent pool',
        color: 'from-blue-500 to-cyan-600'
      },
      {
        icon: Target,
        title: 'Advanced Recruitment Marketing Engine',
        description: 'Comprehensive employer brand marketing with automated campaign management, talent community building, engagement analytics, and candidate experience optimisation.',
        metrics: '250% employer brand reach',
        color: 'from-orange-500 to-red-600'
      },
      {
        icon: Users,
        title: 'Multi-Client Portfolio Management',
        description: 'Sophisticated client management with dedicated portals, bespoke branding, client-specific workflows, and comprehensive account oversight capabilities.',
        metrics: 'Unlimited client capacity',
        color: 'from-teal-500 to-blue-600'
      },
      {
        icon: BarChart3,
        title: 'Real-Time ROI & Performance Analytics',
        description: 'Comprehensive performance metrics with cost-per-hire analysis, source effectiveness tracking, revenue management, and data-driven recruitment optimisation.',
        metrics: 'Data-driven recruitment optimisation',
        color: 'from-indigo-500 to-purple-600'
      },
      {
        icon: Workflow,
        title: 'Automated Interview & Assessment Workflows',
        description: 'Streamlined interview processes with automated scheduling, video interview integration, assessment coordination, and collaborative decision-making frameworks.',
        metrics: '80% scheduling efficiency improvement',
        color: 'from-emerald-500 to-green-600'
      },
      {
        icon: Building,
        title: 'Employer Brand & Talent Community Building',
        description: 'Advanced employer branding tools with talent community management, candidate engagement programmes, and long-term relationship building capabilities.',
        metrics: '400% talent community engagement',
        color: 'from-cyan-500 to-teal-600'
      },
      {
        icon: CheckCircle,
        title: 'Diversity, Equity & Inclusion Tools',
        description: 'Comprehensive D&I recruitment tools with bias detection, inclusive sourcing strategies, diversity analytics, and equitable hiring process management.',
        metrics: '60% improvement in diverse hiring',
        color: 'from-rose-500 to-pink-600'
      },
      {
        icon: Monitor,
        title: 'Mobile-First Candidate Experience',
        description: 'Optimised mobile recruitment platform with responsive candidate portals, mobile application processes, and seamless cross-device recruitment experiences.',
        metrics: '90% mobile application completion',
        color: 'from-violet-500 to-purple-600'
      },
      {
        icon: Sparkles,
        title: 'Predictive Hiring Intelligence',
        description: 'Advanced predictive analytics for hiring success forecasting, retention prediction, quality-of-hire analysis, and data-driven recruitment strategy development.',
        metrics: '95% hiring success prediction accuracy',
        color: 'from-yellow-500 to-orange-600'
      },
      {
        icon: Shield,
        title: 'Compliance & Risk Management',
        description: 'Comprehensive compliance management with GDPR adherence, employment law compliance, right-to-work verification, and risk mitigation throughout the recruitment process.',
        metrics: '100% regulatory compliance',
        color: 'from-red-500 to-red-600'
      },
      {
        icon: HeadphonesIcon,
        title: 'Conversational AI Recruitment Engine',
        description: '24/7 candidate engagement with voice and text automation, WhatsApp integration, intelligent chatbot screening, and natural conversation capabilities across multiple channels.',
        metrics: '500% candidate response improvement',
        color: 'from-emerald-500 to-teal-600'
      },
      {
        icon: Search,
        title: 'Semantic Talent Discovery Platform',
        description: 'Revolutionary AI search across global talent databases with natural language queries, semantic understanding, and intelligent candidate discovery from 30+ sources.',
        metrics: 'Access to 50M+ candidate profiles',
        color: 'from-blue-500 to-purple-600'
      },
      {
        icon: Monitor,
        title: 'Mobile-Native Candidate Experience',
        description: 'Industry-leading mobile recruitment journey with text-to-apply functionality, one-touch applications, and optimised mobile candidate engagement throughout.',
        metrics: '95% mobile completion rates',
        color: 'from-violet-500 to-pink-600'
      },
      {
        icon: FileText,
        title: 'Advanced Skills Assessment Suite',
        description: 'Comprehensive technical and soft skills validation with job simulation testing, code assessments, personality profiling, and practical role-based evaluations.',
        metrics: '90% hiring accuracy improvement',
        color: 'from-orange-500 to-red-600'
      },
      {
        icon: Zap,
        title: 'High-Volume Recruitment Automation',
        description: 'Streamlined mass hiring capabilities with bulk interview scheduling, automated screening workflows, and scalable recruitment process management.',
        metrics: '10x recruitment capacity increase',
        color: 'from-cyan-500 to-blue-600'
      }
    ],
    advancedCapabilities: [
      {
        icon: Brain,
        title: 'Machine Learning Talent Insights & Predictions',
        description: 'Advanced AI-powered candidate intelligence with career trajectory prediction, skill evolution tracking, market positioning analysis, and comprehensive talent forecasting.',
        metrics: 'Predictive talent analytics engine',
        color: 'from-violet-500 to-purple-600'
      },
      {
        icon: Settings,
        title: 'Custom Workflow Automation & Orchestration',
        description: 'Highly configurable recruitment processes with intelligent automation, multi-stage interview coordination, decision-making frameworks, and bespoke workflow management.',
        metrics: '90% process automation efficiency',
        color: 'from-emerald-500 to-green-600'
      },
      {
        icon: Search,
        title: 'Advanced Boolean & Semantic Search',
        description: 'Sophisticated search capabilities with Boolean logic, semantic understanding, natural language processing, and intelligent candidate discovery across multiple databases.',
        metrics: '500% improved search precision',
        color: 'from-blue-500 to-cyan-600'
      },
      {
        icon: Globe,
        title: 'Multi-Language & Cultural Adaptation',
        description: 'Comprehensive internationalisation with multi-language support, cultural adaptation tools, localised recruitment processes, and regional compliance management.',
        metrics: '50+ languages supported',
        color: 'from-teal-500 to-blue-600'
      },
      {
        icon: Database,
        title: 'Enterprise Integration & Data Synchronisation',
        description: 'Seamless integration with enterprise systems including HRIS, CRM, payroll, and business intelligence platforms with real-time data synchronisation capabilities.',
        metrics: '200+ system integrations',
        color: 'from-cyan-500 to-teal-600'
      },
      {
        icon: Building,
        title: 'White-Label Client Portals & Branding',
        description: 'Fully customisable client portals with white-label branding, bespoke user interfaces, client-specific functionality, and comprehensive brand management tools.',
        metrics: 'Unlimited branding customisation',
        color: 'from-purple-500 to-pink-600'
      },
      {
        icon: BarChart3,
        title: 'Advanced Reporting & Business Intelligence',
        description: 'Comprehensive analytics platform with executive dashboards, predictive reporting, business intelligence tools, and strategic recruitment insights.',
        metrics: 'Real-time business intelligence',
        color: 'from-orange-500 to-red-600'
      },
      {
        icon: Users,
        title: 'Real-Time Collaboration & Team Management',
        description: 'Advanced team collaboration tools with real-time updates, role-based permissions, collaborative decision-making, and comprehensive team performance management.',
        metrics: '75% improved team efficiency',
        color: 'from-green-500 to-emerald-600'
      },
      {
        icon: MessageSquare,
        title: 'Candidate Relationship Management (CRM)',
        description: 'Sophisticated CRM functionality with engagement tracking, talent pipelining, nurturing campaigns, and long-term candidate relationship management.',
        metrics: '5x larger active talent pools',
        color: 'from-indigo-500 to-purple-600'
      },
      {
        icon: CheckCircle,
        title: 'Background Check & Reference Automation',
        description: 'Automated background verification with integrated reference checking, compliance validation, right-to-work verification, and comprehensive candidate screening.',
        metrics: '95% faster verification process',
        color: 'from-rose-500 to-pink-600'
      },
      {
        icon: Workflow,
        title: 'Onboarding Process Integration',
        description: 'Seamless onboarding workflow integration with new hire processing, documentation management, training coordination, and employee lifecycle management.',
        metrics: '80% onboarding efficiency improvement',
        color: 'from-yellow-500 to-orange-600'
      },
      {
        icon: TrendingUp,
        title: 'Performance Tracking & Quality Metrics',
        description: 'Comprehensive performance monitoring with quality-of-hire tracking, retention analysis, performance correlation, and continuous improvement metrics.',
        metrics: 'Data-driven quality optimisation',
        color: 'from-emerald-500 to-green-600'
      },
      {
        icon: Target,
        title: 'Market Intelligence & Competitive Analysis',
        description: 'Advanced market intelligence with competitive analysis, salary benchmarking, market trends tracking, and strategic positioning insights.',
        metrics: 'Real-time market intelligence',
        color: 'from-cyan-500 to-teal-600'
      },
      {
        icon: Briefcase,
        title: 'Recruitment Process Outsourcing (RPO) Tools',
        description: 'Comprehensive RPO management tools with client service delivery, SLA tracking, performance management, and scalable recruitment operations support.',
        metrics: 'Enterprise RPO capabilities',
        color: 'from-violet-500 to-purple-600'
      },
      {
        icon: Network,
        title: 'API & Third-Party Integration Platform',
        description: 'Extensive API ecosystem with third-party integrations, custom connector development, data exchange protocols, and comprehensive integration management.',
        metrics: '500+ platform integrations',
        color: 'from-blue-500 to-cyan-600'
      },
      {
        icon: HeadphonesIcon,
        title: 'AI Voice Interview Agents',
        description: 'Natural conversation screening with emotional intelligence, automated voice interviews in 50+ languages, and conversational AI for candidate pre-qualification.',
        metrics: '24/7 voice screening capability',
        color: 'from-green-500 to-emerald-600'
      },
      {
        icon: Phone,
        title: 'WhatsApp & SMS Automation Platform',
        description: 'Multi-channel messaging recruitment with automated candidate engagement, SMS campaigns, WhatsApp Business integration, and conversational hiring workflows.',
        metrics: '300% candidate engagement increase',
        color: 'from-teal-500 to-cyan-600'
      },
      {
        icon: Search,
        title: 'Semantic Search & Discovery Engine',
        description: 'Natural language candidate search across 30+ sources with semantic understanding, AI-powered talent discovery, and intelligent candidate matching algorithms.',
        metrics: 'Revolutionary talent discovery',
        color: 'from-purple-500 to-violet-600'
      },
      {
        icon: FileCheck,
        title: 'Job Simulation & Assessment Platform',
        description: 'Role-specific practical testing environment with job simulations, technical assessments, cognitive testing, and comprehensive skills validation tools.',
        metrics: 'Predictive hiring accuracy',
        color: 'from-rose-500 to-pink-600'
      },
      {
        icon: Monitor,
        title: 'Mobile-First Application Pipeline',
        description: 'One-touch apply with mobile optimisation, text-to-apply functionality, responsive candidate portals, and seamless cross-device recruitment experiences.',
        metrics: 'Mobile-optimised hiring',
        color: 'from-indigo-500 to-purple-600'
      },
      {
        icon: FileText,
        title: 'Resume Builder & Optimisation Suite',
        description: 'Integrated CV creation and ATS scoring with resume enhancement tools, career progression mapping, and candidate profile optimisation capabilities.',
        metrics: 'Enhanced candidate profiles',
        color: 'from-yellow-500 to-orange-600'
      },
      {
        icon: Calendar,
        title: 'High-Volume Interview Orchestration',
        description: 'Bulk scheduling and coordination automation with mass interview management, automated calendar coordination, and scalable interview process workflows.',
        metrics: '500% scheduling efficiency',
        color: 'from-emerald-500 to-green-600'
      },
      {
        icon: TrendingUp,
        title: 'Real-Time Market Intelligence Dashboard',
        description: 'Live salary and competitive data with market analysis, recruitment trends, competitive intelligence, and strategic positioning insights.',
        metrics: 'Real-time market insights',
        color: 'from-cyan-500 to-teal-600'
      },
      {
        icon: Sparkles,
        title: 'Automated Decision-Making Engine',
        description: 'AI-powered hiring recommendations with confidence scoring, automated candidate ranking, intelligent decision support, and predictive hiring intelligence.',
        metrics: 'Intelligent hiring decisions',
        color: 'from-violet-500 to-purple-600'
      },
      {
        icon: Briefcase,
        title: 'Dynamic Content Generation',
        description: 'AI-powered job descriptions and communications with automated content creation, personalised messaging, and intelligent recruitment marketing materials.',
        metrics: 'Automated content creation',
        color: 'from-blue-500 to-cyan-600'
      }
    ],
    securityPerformance: [
      {
        icon: Shield,
        title: 'Compliance Excellence Framework',
        description: 'SOC 2 Type II, ISO 27001, GDPR compliance with continuous auditing, regulatory reporting, and industry standard certifications.',
        metrics: 'Enterprise-grade compliance',
        color: 'from-red-500 to-red-600'
      },
      {
        icon: BarChart3,
        title: 'Security Performance Analytics',
        description: 'Real-time threat monitoring, security incident analytics, and performance metrics with advanced dashboard visualization.',
        metrics: '24/7 security monitoring',
        color: 'from-blue-500 to-cyan-600'
      },
      {
        icon: Zap,
        title: 'Scalable Security Infrastructure',
        description: '99.99% uptime guarantee with auto-scaling security services, load balancing, and distributed protection mechanisms.',
        metrics: 'Guaranteed system availability',
        color: 'from-green-500 to-emerald-600'
      },
      {
        icon: Globe,
        title: 'Advanced Firewall & DDoS Protection',
        description: 'Enterprise-grade firewall protection with intelligent DDoS mitigation, traffic filtering, and threat prevention.',
        metrics: 'Multi-layer threat protection',
        color: 'from-purple-500 to-pink-600'
      },
      {
        icon: Lock,
        title: 'Multi-Factor Authentication Suite',
        description: 'Comprehensive MFA with biometric authentication, hardware tokens, and adaptive authentication protocols.',
        metrics: 'Advanced identity verification',
        color: 'from-orange-500 to-red-600'
      },
      {
        icon: Target,
        title: 'Security Information & Event Management',
        description: 'AI-powered SIEM with real-time monitoring, automated incident response, and comprehensive security orchestration.',
        metrics: 'Intelligent threat detection',
        color: 'from-teal-500 to-blue-600'
      },
      {
        icon: Database,
        title: 'Data Loss Prevention & Backup',
        description: 'Encrypted data protection with automated backups, disaster recovery, and point-in-time restoration capabilities.',
        metrics: 'Zero data loss guarantee',
        color: 'from-indigo-500 to-purple-600'
      },
      {
        icon: Settings,
        title: 'Network Segmentation & Monitoring',
        description: 'Advanced network isolation with micro-segmentation, traffic analysis, and real-time network security monitoring.',
        metrics: 'Enhanced network security',
        color: 'from-cyan-500 to-teal-600'
      },
      {
        icon: CheckCircle,
        title: 'Continuous Security Assessment',
        description: 'Automated vulnerability scanning, penetration testing, and continuous security validation with remediation guidance.',
        metrics: 'Proactive security testing',
        color: 'from-yellow-500 to-orange-600'
      }
    ]
  },

  'recruitment-analytics-platform': {
    keyBenefits: [
      {
        icon: TrendingUp,
        title: 'Advanced Recruitment Analytics',
        description: 'Comprehensive recruitment intelligence with predictive analytics, trend analysis, and performance optimization.',
        metrics: '90% improvement in hiring decisions',
        color: 'from-orange-500 to-red-600'
      },
      {
        icon: BarChart3,
        title: 'Real-Time Performance Dashboard',
        description: 'Live recruitment metrics with customizable dashboards, KPI tracking, and executive reporting.',
        metrics: 'Real-time recruitment insights',
        color: 'from-blue-500 to-cyan-600'
      },
      {
        icon: Brain,
        title: 'Predictive Hiring Intelligence',
        description: 'Machine learning for candidate success prediction, retention forecasting, and quality-of-hire analysis.',
        metrics: '95% hiring success prediction',
        color: 'from-purple-500 to-pink-600'
      },
      {
        icon: Target,
        title: 'Source Effectiveness Analysis',
        description: 'Comprehensive channel performance with ROI analysis, cost optimization, and sourcing strategy recommendations.',
        metrics: 'Optimized sourcing investments',
        color: 'from-green-500 to-emerald-600'
      },
      {
        icon: Users,
        title: 'Diversity & Inclusion Metrics',
        description: 'Advanced D&I analytics with bias detection, representation tracking, and inclusive hiring insights.',
        metrics: 'Comprehensive D&I visibility',
        color: 'from-teal-500 to-blue-600'
      },
      {
        icon: Globe,
        title: 'Competitive Benchmarking',
        description: 'Market comparison analytics with industry benchmarks, salary intelligence, and competitive positioning.',
        metrics: 'Industry-leading insights',
        color: 'from-indigo-500 to-purple-600'
      }
    ],
    advancedCapabilities: [
      {
        icon: Sparkles,
        title: 'AI-Powered Insights Engine',
        description: 'Advanced analytics with natural language queries, automated insights, and predictive recommendations.',
        metrics: 'Intelligent analytics automation',
        color: 'from-violet-500 to-purple-600'
      },
      {
        icon: Settings,
        title: 'Custom Analytics Framework',
        description: 'Configurable metrics and KPIs with custom calculations, personalized dashboards, and flexible reporting.',
        metrics: 'Tailored analytics solutions',
        color: 'from-emerald-500 to-green-600'
      },
      {
        icon: FileText,
        title: 'Executive Reporting Suite',
        description: 'Automated executive reports with strategic insights, trend analysis, and actionable recommendations.',
        metrics: 'C-level strategic insights',
        color: 'from-cyan-500 to-teal-600'
      },
      {
        icon: Calendar,
        title: 'Time-to-Hire Optimization',
        description: 'Detailed hiring timeline analysis with bottleneck identification and process optimization recommendations.',
        metrics: '50% faster hiring cycles',
        color: 'from-yellow-500 to-orange-600'
      },
      {
        icon: Database,
        title: 'Historical Data Intelligence',
        description: 'Long-term trend analysis with historical comparisons, seasonal patterns, and cyclical insights.',
        metrics: 'Multi-year trend analysis',
        color: 'from-rose-500 to-pink-600'
      },
      {
        icon: CheckCircle,
        title: 'Performance Correlation Engine',
        description: 'Advanced correlation analysis between recruitment metrics and business outcomes with impact measurement.',
        metrics: 'Business impact visibility',
        color: 'from-blue-500 to-cyan-600'
      }
    ],
    securityPerformance: [
      {
        icon: Shield,
        title: 'Compliance Excellence Framework',
        description: 'SOC 2 Type II, ISO 27001, GDPR compliance with continuous auditing, regulatory reporting, and industry standard certifications.',
        metrics: 'Enterprise-grade compliance',
        color: 'from-red-500 to-red-600'
      },
      {
        icon: BarChart3,
        title: 'Security Performance Analytics',
        description: 'Real-time threat monitoring, security incident analytics, and performance metrics with advanced dashboard visualization.',
        metrics: '24/7 security monitoring',
        color: 'from-blue-500 to-cyan-600'
      },
      {
        icon: Zap,
        title: 'Scalable Security Infrastructure',
        description: '99.99% uptime guarantee with auto-scaling security services, load balancing, and distributed protection mechanisms.',
        metrics: 'Guaranteed system availability',
        color: 'from-green-500 to-emerald-600'
      },
      {
        icon: Globe,
        title: 'Advanced Firewall & DDoS Protection',
        description: 'Enterprise-grade firewall protection with intelligent DDoS mitigation, traffic filtering, and threat prevention.',
        metrics: 'Multi-layer threat protection',
        color: 'from-purple-500 to-pink-600'
      },
      {
        icon: Lock,
        title: 'Multi-Factor Authentication Suite',
        description: 'Comprehensive MFA with biometric authentication, hardware tokens, and adaptive authentication protocols.',
        metrics: 'Advanced identity verification',
        color: 'from-orange-500 to-red-600'
      },
      {
        icon: Target,
        title: 'Security Information & Event Management',
        description: 'AI-powered SIEM with real-time monitoring, automated incident response, and comprehensive security orchestration.',
        metrics: 'Intelligent threat detection',
        color: 'from-teal-500 to-blue-600'
      },
      {
        icon: Database,
        title: 'Data Loss Prevention & Backup',
        description: 'Encrypted data protection with automated backups, disaster recovery, and point-in-time restoration capabilities.',
        metrics: 'Zero data loss guarantee',
        color: 'from-indigo-500 to-purple-600'
      },
      {
        icon: Settings,
        title: 'Network Segmentation & Monitoring',
        description: 'Advanced network isolation with micro-segmentation, traffic analysis, and real-time network security monitoring.',
        metrics: 'Enhanced network security',
        color: 'from-cyan-500 to-teal-600'
      },
      {
        icon: CheckCircle,
        title: 'Continuous Security Assessment',
        description: 'Automated vulnerability scanning, penetration testing, and continuous security validation with remediation guidance.',
        metrics: 'Proactive security testing',
        color: 'from-yellow-500 to-orange-600'
      }
    ]
  }
};